/* Missioni v17 — clean map + bottom drawer with 2 modes (Crea/Da altri) +
   real-time visibility toggle + heartbeat. Solo missioni / pericoli / SOS
   (no vet, no rifugi, no parchi cane). */
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { useNavigate } from "react-router-dom";
import {
  Camera, Flag, AlertTriangle, Eye, EyeOff, Settings2, ChevronUp, ChevronDown,
  Users, Sparkles, PawPrint, Plus, RefreshCcw, MapPin, Trash2, Crown, Lock,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { hapticTap, hapticSuccess } from "@/lib/haptics";
import QuickHazardCapture from "@/components/QuickHazardCapture";
import { CreateMissionSheet } from "@/pages/MissionsHub";

const DEFAULT_CENTER = [45.4642, 9.1900];
const HEARTBEAT_MS = 60_000;
const FEED_REFRESH_MS = 45_000;

// ────── Icons ──────
const dot = (color, glyph) => L.divIcon({
  className: "",
  html: `<div style="position:relative;height:46px;width:46px;display:flex;align-items:center;justify-content:center">
    <div style="position:absolute;inset:0;border-radius:50%;background:radial-gradient(circle,${color}55 0%,transparent 70%);animation:orio-ping 2.4s ease-out infinite"></div>
    <div style="position:relative;height:32px;width:32px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 6px 18px -4px ${color}88;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:14px;line-height:1">${glyph}</div>
  </div>`,
  iconSize: [46, 46], iconAnchor: [23, 23],
});
const hazardIcon  = dot("#E94545", "⚠");
const missionIcon = dot("#FF7A1A", "🚩");
const sosIcon     = dot("#7B61FF", "S");
const meIcon = L.divIcon({
  className: "",
  html: `<div style="height:18px;width:18px;border-radius:50%;background:#0EA5E9;border:3px solid #fff;box-shadow:0 0 0 4px rgba(14,165,233,0.30)"></div>`,
  iconSize: [18, 18], iconAnchor: [9, 9],
});
const peerIcon = (photo) => L.divIcon({
  className: "",
  html: `<div style="height:38px;width:38px;border-radius:50%;background:#fff;border:3px solid #2DBA60;box-shadow:0 4px 12px -2px rgba(45,186,96,0.45);overflow:hidden">
    ${photo ? `<img src="${photo}" style="width:100%;height:100%;object-fit:cover" />`
            : `<div style="width:100%;height:100%;background:linear-gradient(135deg,#34D399,#2DBA60);display:flex;align-items:center;justify-content:center;color:#fff;font-size:14px;font-weight:700">🐾</div>`}
  </div>`,
  iconSize: [38, 38], iconAnchor: [19, 19],
});

function Recenter({ c }) {
  const map = useMap();
  const didCenterRef = React.useRef(false);
  useEffect(() => {
    if (!c) return;
    // Only auto-center ONCE (initial geolocation refinement). After that, the
    // user is in control — never snap back after pan/zoom on subsequent
    // heartbeat updates. Fix for "map moves by itself" bug.
    if (didCenterRef.current) return;
    map.setView(c, map.getZoom() || 14);
    didCenterRef.current = true;
  }, [c, map]);
  return null;
}

export default function Missioni() {
  const { user } = useAuth();
  const { isPro, canStartTrial, startTrial } = useSubscription();
  const navigate = useNavigate();
  // Open immediately on the user's last known position and refine with live geolocation.
  // Priority: user.location.{lat,lng} (registered home — stable) → map_last_lat/lng
  // (heartbeat — can drift / be stale) → Milan only when nothing is known.
  const knownPos = (() => {
    const loc = user?.location || {};
    if (typeof loc.lat === "number" && typeof loc.lng === "number") return [loc.lat, loc.lng];
    const ll = user?.map_last_lat;
    const lg = user?.map_last_lng;
    if (typeof ll === "number" && typeof lg === "number") return [ll, lg];
    return null;
  })();
  const [center, setCenter] = useState(knownPos || DEFAULT_CENTER);
  const [me, setMe] = useState(knownPos);
  const [feed, setFeed] = useState({ items: [], counts: { hazards: 0, missions: 0, sos: 0, total: 0 } });
  const [peers, setPeers] = useState([]);
  const [mode, setMode] = useState("others"); // "create" | "others"
  const [visible, setVisible] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [optsOpen, setOptsOpen] = useState(false);
  const [quickHazardOpen, setQuickHazardOpen] = useState(false);
  const [missionCreateOpen, setMissionCreateOpen] = useState(false);
  const [proGateOpen, setProGateOpen] = useState(false);
  const hbRef = useRef(null);

  const guardPro = (fn) => () => {
    if (!isPro) {
      hapticTap();
      setProGateOpen(true);
      return;
    }
    fn();
  };

  // Initial geo + load
  useEffect(() => {
    // If we already have a known position, fetch feed immediately so the
    // user sees their zone the moment Missioni opens.
    if (knownPos) refresh(knownPos[0], knownPos[1]);
    if (!navigator.geolocation) {
      if (!knownPos) refresh(DEFAULT_CENTER[0], DEFAULT_CENTER[1]);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const c = [p.coords.latitude, p.coords.longitude];
        setCenter(c); setMe(c); refresh(c[0], c[1]);
      },
      () => { if (!knownPos) refresh(DEFAULT_CENTER[0], DEFAULT_CENTER[1]); },
      { enableHighAccuracy: true, timeout: 6000 }
    );
    // load current visibility
    api.get("/me/visibility").then((r) => setVisible(!!r.data.visible)).catch(() => {});
  }, []);

  // Auto-refresh feed every 45s
  useEffect(() => {
    if (!me) return;
    const t = setInterval(() => refresh(me[0], me[1]), FEED_REFRESH_MS);
    // Also refresh immediately whenever the tab / PWA becomes visible again
    const onVis = () => { if (document.visibilityState === "visible") refresh(me[0], me[1]); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [me, mode]);

  // Heartbeat when visible
  useEffect(() => {
    if (hbRef.current) { clearInterval(hbRef.current); hbRef.current = null; }
    if (!visible || !me) return;
    const ping = () => api.post("/me/heartbeat", { lat: me[0], lng: me[1] }).catch(() => {});
    ping();
    hbRef.current = setInterval(ping, HEARTBEAT_MS);
    return () => { if (hbRef.current) clearInterval(hbRef.current); };
  }, [visible, me]);

  const refresh = async (lat, lng) => {
    try {
      const radius = user?.radius_prefs?.missions || 25;
      const [feedRes, peersRes] = await Promise.all([
        api.get("/missioni/live", { params: { lat, lng, radius_km: radius, mine: mode === "create" } }),
        visible
          ? api.get("/missioni/visible-users", { params: { lat, lng, radius_km: 15 } })
          : Promise.resolve({ data: { items: [] } }),
      ]);
      setFeed(feedRes.data);
      setPeers(peersRes.data.items || []);
    } catch {/* ignore */}
  };

  const toggleVisibility = async () => {
    hapticTap();
    const next = !visible;
    try {
      await api.put("/me/visibility", { visible: next, lat: me?.[0], lng: me?.[1] });
      setVisible(next);
      if (next) { hapticSuccess(); toast.success("Sei visibile sulla mappa"); }
      else toast.success("Modalità invisibile attiva");
      if (me) refresh(me[0], me[1]);
    } catch { toast.error("Errore aggiornamento visibilità"); }
  };

  return (
    <div className="fixed inset-0 bg-[#FBF7F1]" data-testid="missioni-page">
      {/* MAP */}
      <div className="absolute inset-0">
        <MapContainer center={center} zoom={14} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
          <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap contributors' maxZoom={19} keepBuffer={6} updateWhenIdle={false} updateWhenZooming={false} />
          <Recenter c={me} />
          {me && <Marker position={me} icon={meIcon} />}
          {/* Hazards */}
          {feed.items.filter(i => i.kind === "hazard").map((h) => {
            const isOwner = !!(user?.id && h?.reported_by && h.reported_by === user.id);
            return (
              <Marker key={`h-${h.id}`} position={[h.lat, h.lng]} icon={hazardIcon}>
                <Popup>
                  <div style={{ minWidth: 200, fontFamily: "ui-sans-serif" }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#E94545" }}>⚠ Pericolo</div>
                    <div style={{ fontWeight: 600, fontSize: 13, margin: "4px 0 2px" }}>{h.description || h.type}</div>
                    <div style={{ fontSize: 11, color: "#6B6B6B" }}>{h.distance_km} km</div>
                    {isOwner && (
                      <button
                        data-testid={`hz-remove-mine-${h.id}`}
                        onClick={async () => {
                          if (!window.confirm("Rimuovere questo pericolo dalla mappa? Sarà eliminato per tutti.")) return;
                          try {
                            await api.delete(`/hazards/${h.id}`);
                            toast.success("Pericolo rimosso dalla mappa");
                            if (me) refresh(me[0], me[1]);
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Errore rimozione");
                          }
                        }}
                        style={{
                          marginTop: 10, width: "100%", padding: "6px 10px",
                          border: 0, borderRadius: 8,
                          background: "#FEE2E2", color: "#E94545",
                          fontSize: 12, fontWeight: 600, cursor: "pointer",
                          display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                        }}
                      >
                        🗑 Rimuovi (il tuo)
                      </button>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}
          {/* Missions */}
          {feed.items.filter(i => i.kind === "mission").map((m) => (
            <Marker key={`m-${m.id}`} position={[m.lat, m.lng]} icon={missionIcon}>
              <Popup>
                <div style={{ minWidth: 180, fontFamily: "ui-sans-serif" }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#FF7A1A" }}>🚩 Missione</div>
                  <div style={{ fontWeight: 600, fontSize: 13, margin: "4px 0 2px" }}>{m.title}</div>
                  <div style={{ fontSize: 11, color: "#6B6B6B" }}>{m.distance_km} km</div>
                </div>
              </Popup>
            </Marker>
          ))}
          {/* SOS */}
          {feed.items.filter(i => i.kind === "sos").map((s) => (
            <Marker key={`s-${s.id}`} position={[s.lat, s.lng]} icon={sosIcon}>
              <Popup>
                <div style={{ minWidth: 180, fontFamily: "ui-sans-serif" }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#7B61FF" }}>SOS attivo</div>
                  <div style={{ fontSize: 12, margin: "4px 0 2px", color: "#1A1A1A" }}>{s.dog_name || "Cane smarrito"}</div>
                  <div style={{ fontSize: 11, color: "#6B6B6B" }}>{s.distance_km} km</div>
                </div>
              </Popup>
            </Marker>
          ))}
          {/* Online users (only if I'm visible too) */}
          {visible && peers.map((u) => (
            <Marker key={`u-${u.id}`} position={[u.lat, u.lng]} icon={peerIcon(u.photo_url)}>
              <Popup>
                <div style={{ minWidth: 180, fontFamily: "ui-sans-serif" }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#2DBA60" }}>🟢 Online</div>
                  <div style={{ fontWeight: 600, fontSize: 13, margin: "4px 0 2px" }}>{u.name}</div>
                  <div style={{ fontSize: 11, color: "#6B6B6B" }}>{u.distance_km} km</div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Top status pill */}
      <div className="absolute top-[68px] md:top-[76px] left-1/2 -translate-x-1/2 z-[600]">
        <motion.div
          initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          className="px-3 py-1.5 rounded-full bg-white/95 backdrop-blur border border-[#F0EBE3] inline-flex items-center gap-2 text-[11px] font-semibold"
          style={{ boxShadow: "0 8px 22px -10px rgba(0,0,0,0.15)" }}
          data-testid="missioni-top-status"
        >
          <span className="h-2 w-2 rounded-full bg-[#2DBA60] animate-pulse" /> Live
          <span className="text-[#9A9A9A]">·</span>
          <span className="text-[#E94545]">{feed.counts.hazards} pericoli</span>
          <span className="text-[#9A9A9A]">·</span>
          <span className="text-[#FF7A1A]">{feed.counts.missions} missioni</span>
          {feed.counts.sos > 0 && (<>
            <span className="text-[#9A9A9A]">·</span>
            <span className="text-[#7B61FF]">{feed.counts.sos} SOS</span>
          </>)}
        </motion.div>
      </div>

      {/* Live peers preview — collapsible card, shows online users in 15km.
          Now non-invasive: shows only a tiny pill by default. */}
      {visible && peers.length > 0 && (
        <LivePeersPreview peers={peers} />
      )}

      {/* Right-side action buttons (Opts + Recenter) */}
      <div className="absolute top-[68px] md:top-[76px] right-3 z-[600] flex flex-col gap-2">
        <button onClick={() => { hapticTap(); setOptsOpen(o => !o); }} data-testid="missioni-opts-toggle"
          className="h-11 w-11 rounded-full bg-white border border-[#F0EBE3] flex items-center justify-center hover:bg-[#F4EFE6]"
          style={{ boxShadow: "0 6px 18px -8px rgba(0,0,0,0.20)" }}
          aria-label="Opzioni">
          <Settings2 className="h-4 w-4 text-[#1A1A1A]" />
        </button>
        <button
          onClick={() => {
            hapticTap();
            if (!navigator.geolocation) { toast.error("Geolocalizzazione non disponibile"); return; }
            toast.message("Sto cercando la tua posizione…");
            navigator.geolocation.getCurrentPosition(
              (p) => {
                const c = [p.coords.latitude, p.coords.longitude];
                setCenter(c); setMe(c); refresh(c[0], c[1]); hapticSuccess();
                toast.success("Posizione aggiornata");
              },
              () => toast.error("Impossibile ottenere la posizione · controlla i permessi"),
              { enableHighAccuracy: true, timeout: 8000 }
            );
          }}
          data-testid="missioni-recenter"
          className="h-11 w-11 rounded-full bg-white border border-[#F0EBE3] flex items-center justify-center hover:bg-[#F4EFE6]"
          style={{ boxShadow: "0 6px 18px -8px rgba(0,0,0,0.20)" }}
          aria-label="La mia posizione">
          <MapPin className="h-4 w-4 text-[#FF7A1A]" />
        </button>
        <AnimatePresence>
          {optsOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: -6 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
              className="absolute top-[108px] right-0 w-[230px] rounded-2xl p-2.5 bg-white border border-[#F0EBE3]"
              style={{ boxShadow: "0 14px 36px -10px rgba(0,0,0,0.25)" }}
              data-testid="missioni-opts-panel"
            >
              <button onClick={toggleVisibility} data-testid="toggle-visibility"
                className="w-full flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-[#FBF7F1] transition-colors text-left">
                <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: visible ? "#2DBA6020" : "#F4EFE6", color: visible ? "#2DBA60" : "#9A9A9A" }}>
                  {visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-semibold text-[#1A1A1A]">
                    {visible ? "Sei visibile" : "Sii visibile"}
                  </div>
                  <div className="text-[10px] text-[#6B6B6B] leading-tight">
                    {visible ? "Vedi e sei visto in tempo reale" : "Mostrati sulla mappa per vedere gli altri"}
                  </div>
                </div>
                <div className="shrink-0 h-5 w-9 rounded-full flex items-center px-0.5 transition-colors"
                  style={{ background: visible ? "#2DBA60" : "#E5DECF" }}>
                  <motion.div animate={{ x: visible ? 16 : 0 }} className="h-4 w-4 rounded-full bg-white" />
                </div>
              </button>
              <div className="mt-1 px-2 py-1.5 rounded-lg text-[10.5px] text-[#9A9A9A] leading-snug">
                {peers.length > 0 && visible
                  ? <><Users className="inline h-3 w-3 mr-1 text-[#2DBA60]" /> {peers.length} padroni online in zona</>
                  : "Quando attivi la visibilità, condividi la tua posizione approssimativa con la community."}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* BOTTOM DRAWER */}
      <motion.div
        initial={false}
        animate={{ y: drawerOpen ? 0 : "calc(100% - 64px)" }}
        transition={{ type: "spring", damping: 28, stiffness: 240 }}
        className="absolute inset-x-0 bottom-0 z-[700] bg-white rounded-t-[28px] border-t border-x border-[#F0EBE3] flex flex-col"
        style={{
          maxHeight: "min(72dvh, calc(100dvh - 80px))",
          boxShadow: "0 -16px 40px -12px rgba(0,0,0,0.18)",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 168px)",
        }}
        data-testid="missioni-drawer"
      >
        {/* Handle */}
        <button onClick={() => { hapticTap(); setDrawerOpen(o => !o); }} data-testid="drawer-toggle"
          className="mx-auto mt-2.5 mb-1 px-6 py-1.5 -mt-px flex flex-col items-center gap-0.5"
          aria-label="Apri / chiudi pannello">
          <div className="h-1 w-10 rounded-full bg-[#E5DECF]" />
          {drawerOpen ? <ChevronDown className="h-3 w-3 text-[#9A9A9A]" /> : <ChevronUp className="h-3 w-3 text-[#9A9A9A]" />}
        </button>

        {/* Mode tabs */}
        <div className="px-4 pb-2">
          <div className="bg-[#F4EFE6] rounded-full p-1 inline-flex w-full" data-testid="missioni-mode-tabs">
            {[
              { id: "create",  label: "Crea / Segnala", icon: Plus },
              { id: "others",  label: "Da altri",       icon: Sparkles },
            ].map((t) => {
              const active = mode === t.id;
              const Ic = t.icon;
              return (
                <button
                  key={t.id}
                  data-testid={`mode-${t.id}`}
                  onClick={() => { hapticTap(); setMode(t.id); if (me) refresh(me[0], me[1]); }}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-full text-[12.5px] font-semibold transition-colors"
                  style={{
                    background: active ? "#FFFFFF" : "transparent",
                    color: active ? "#FF7A1A" : "#6B6B6B",
                    boxShadow: active ? "0 4px 12px -4px rgba(0,0,0,0.10)" : "none",
                  }}
                >
                  <Ic className="h-3.5 w-3.5" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Drawer content */}
        <div className="flex-1 overflow-y-auto px-4 pt-1 pb-2">
          {mode === "create" ? (
            <CreateMode
              isPro={isPro}
              onHazard={() => { hapticTap(); setQuickHazardOpen(true); }}
              onMission={guardPro(() => { hapticTap(); setMissionCreateOpen(true); })}
            />
          ) : (
            <OthersFeed feed={feed} onRefresh={() => me && refresh(me[0], me[1])} />
          )}
        </div>
      </motion.div>

      {/* Quick hazard sheet */}
      <QuickHazardCapture
        open={quickHazardOpen}
        onClose={() => setQuickHazardOpen(false)}
        onDone={() => { setQuickHazardOpen(false); if (me) refresh(me[0], me[1]); }}
      />
      {/* Create mission sheet (riusato da MissionsHub) */}
      {missionCreateOpen && (
        <CreateMissionSheet
          onClose={() => setMissionCreateOpen(false)}
          onDone={() => { setMissionCreateOpen(false); if (me) refresh(me[0], me[1]); }}
          role={user?.role === "shelter" ? "shelter" : "user"}
          userPos={me ? { lat: me[0], lng: me[1] } : null}
        />
      )}

      {/* Pro paywall for free users trying to create/participate */}
      <AnimatePresence>
        {proGateOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1700] flex items-end sm:items-center justify-center"
            data-testid="missioni-pro-paywall"
            onClick={() => setProGateOpen(false)}
          >
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <motion.div
              initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }}
              transition={{ type: "spring", damping: 26, stiffness: 280 }}
              className="relative w-full max-w-md mx-3 rounded-t-[28px] sm:rounded-[28px] border border-[#F0EBE3] bg-white p-6 overflow-hidden"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div aria-hidden className="absolute -top-24 -right-16 h-64 w-64 rounded-full opacity-30"
                style={{ background: "radial-gradient(circle, rgba(255,176,32,0.55) 0%, transparent 65%)" }} />
              <div className="relative flex items-start gap-3">
                <div className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 text-white"
                  style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #FFB020 100%)" }}>
                  <Crown className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-[0.20em] font-bold text-[#C25612] inline-flex items-center gap-1.5">
                    <Lock className="h-3 w-3" /> Funzione ORIO Pro
                  </div>
                  <h3 className="font-display text-2xl leading-tight text-[#1A1A1A] mt-0.5">
                    Le missioni ORIO Pro sono riservate agli abbonati.
                  </h3>
                  <p className="text-[13px] text-[#6B6B6B] mt-1.5 leading-snug">
                    Con ORIO Pro puoi creare missioni e partecipare a quelle degli altri.
                    Segnalare i pericoli è gratis: le missioni no.
                  </p>
                </div>
              </div>
              <motion.button
                whileTap={{ scale: 0.97 }}
                data-testid="missioni-pro-cta"
                onClick={() => {
                  setProGateOpen(false);
                  if (canStartTrial) startTrial();
                  else navigate("/billing");
                }}
                className="mt-5 w-full py-3.5 rounded-full text-white font-semibold text-[14px] inline-flex items-center justify-center gap-2"
                style={{
                  background: "linear-gradient(135deg, #FF7A1A 0%, #E94545 100%)",
                  boxShadow: "0 16px 32px -12px rgba(233,69,69,0.55)",
                }}
              >
                <Sparkles className="h-4 w-4" />
                {canStartTrial ? "Inizia 30 giorni gratis" : "Abbonati a ORIO Pro"}
              </motion.button>
              <button
                type="button"
                onClick={() => setProGateOpen(false)}
                data-testid="missioni-pro-later"
                className="mt-2 w-full py-2 text-[12px] text-[#9A9A9A] hover:text-[#1A1A1A]"
              >
                Continua a esplorare
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const ProBadge = () => (
  <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-[0.14em] text-white shrink-0"
    style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #FFB020 100%)" }}>
    <Crown className="h-2.5 w-2.5" /> Pro
  </span>
);

/* ───── Create mode ───── */
function CreateMode({ isPro, onHazard, onMission }) {
  return (
    <div className="space-y-2.5 pt-1" data-testid="create-mode">
      <div className="text-[10px] uppercase tracking-[0.20em] text-[#9A9A9A] font-semibold mb-1">
        Cosa vuoi pubblicare?
      </div>
      <motion.button whileTap={{ scale: 0.985 }} data-testid="create-hazard-btn" onClick={onHazard}
        className="w-full flex items-center gap-3 p-3.5 rounded-2xl border border-[#F0EBE3] bg-white hover:bg-[#FBF7F1] text-left"
        style={{ boxShadow: "0 6px 18px -12px rgba(255,90,40,0.40)" }}>
        <div className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0 text-white"
          style={{ background: "linear-gradient(135deg, #FFB020 0%, #FF3B30 100%)", boxShadow: "0 10px 20px -8px rgba(255,59,48,0.5)" }}>
          <Camera className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg text-[#1A1A1A] leading-tight">Segnala un pericolo</div>
          <div className="text-[11.5px] text-[#6B6B6B] leading-snug">Foto + tipo = altri padroni avvisati nel raggio · +3 karma</div>
        </div>
      </motion.button>
      <motion.button whileTap={{ scale: 0.985 }} data-testid="create-mission-btn" onClick={onMission}
        className="w-full flex items-center gap-3 p-3.5 rounded-2xl border border-[#F0EBE3] bg-white hover:bg-[#FBF7F1] text-left">
        <div className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0 text-white"
          style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)", boxShadow: "0 10px 20px -8px rgba(255,122,26,0.45)" }}>
          <Flag className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-display text-lg text-[#1A1A1A] leading-tight">Crea una missione</div>
            {!isPro && <ProBadge />}
          </div>
          <div className="text-[11.5px] text-[#6B6B6B] leading-snug">Pulizia di un parco, raduno, aiuto a un cane in difficoltà</div>
        </div>
      </motion.button>
      <div className="px-1 pt-3 text-[10.5px] text-[#9A9A9A]">
        {isPro
          ? "Le missioni chiuse spariscono per sempre dalla mappa e dalla bacheca."
          : "Piano gratuito: puoi vedere e segnalare i pericoli. Creare o partecipare alle missioni richiede ORIO Pro."}
      </div>
    </div>
  );
}

/* ───── Others feed ───── */
function OthersFeed({ feed, onRefresh }) {
  const items = feed.items.filter(i => i.kind !== "sos"); // SOS sono sempre visibili sopra
  const sos = feed.items.filter(i => i.kind === "sos");
  return (
    <div data-testid="others-feed">
      {sos.length > 0 && (
        <div className="mb-3" data-testid="others-sos">
          {sos.map((s) => (
            <div key={s.id} className="rounded-2xl p-3 border border-[#7B61FF]/30 mb-1.5"
              style={{ background: "linear-gradient(135deg, rgba(123,97,255,0.08) 0%, rgba(255,255,255,0.95) 100%)" }}>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#7B61FF]">SOS attivo</div>
              <div className="text-[14px] font-semibold text-[#1A1A1A] leading-tight mt-0.5">{s.dog_name || "Cane smarrito"}</div>
              <div className="text-[11px] text-[#6B6B6B] mt-0.5">{s.distance_km} km · {fmtAgo(s.created_at)}</div>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-[0.20em] font-semibold text-[#9A9A9A]">
          Segnalazioni vicine
        </div>
        <button onClick={onRefresh} aria-label="Aggiorna" data-testid="feed-refresh"
          className="text-[#9A9A9A] hover:text-[#1A1A1A] -m-1 p-1">
          <RefreshCcw className="h-3.5 w-3.5" />
        </button>
      </div>
      {items.length === 0 ? (
        <div className="rounded-2xl p-6 text-center text-[#9A9A9A] text-sm bg-[#FBF7F1] border border-dashed border-[#E5DECF]">
          Tutto tranquillo qui intorno. 🐾
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((i) => (
            <div key={`${i.kind}-${i.id}`} className="flex items-center gap-2.5 p-2.5 rounded-2xl bg-white border border-[#F0EBE3]"
              data-testid={`others-row-${i.kind}-${i.id}`}>
              <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0 text-white text-[14px]"
                style={{ background: i.kind === "hazard" ? "linear-gradient(135deg, #E94545, #FF7A1A)" : "linear-gradient(135deg, #FF7A1A, #F4B83C)" }}>
                {i.kind === "hazard" ? "⚠" : "🚩"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[13.5px] text-[#1A1A1A] leading-tight truncate">
                  {i.kind === "hazard" ? (i.description || "Pericolo") : i.title}
                </div>
                <div className="text-[10.5px] text-[#6B6B6B] inline-flex items-center gap-1">
                  <MapPin className="h-2.5 w-2.5" /> {i.distance_km} km · {fmtAgo(i.created_at)}
                </div>
              </div>
              {i.photo_url && (
                <img src={i.photo_url} alt="" className="h-9 w-9 rounded-lg object-cover shrink-0" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function fmtAgo(iso) {
  if (!iso) return "";
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return "ora";
  if (sec < 3600) return `${Math.floor(sec / 60)}m fa`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h fa`;
  return `${Math.floor(sec / 86400)}g fa`;
}


/* Collapsible peers preview — sits under the top status pill on Missioni.
   Only rendered when the user has visibility ON (parent guards). */
function LivePeersPreview({ peers }) {
  const [open, setOpen] = React.useState(false);
  const count = peers?.length || 0;
  return (
    <div className="absolute top-[104px] md:top-[112px] left-1/2 -translate-x-1/2 z-[599] w-[320px] max-w-[calc(100vw-24px)] pointer-events-auto">
      <div
        className="rounded-2xl overflow-hidden border border-white/60 backdrop-blur-xl"
        style={{ background: "rgba(255,255,255,0.94)", boxShadow: "0 16px 32px -14px rgba(0,0,0,0.18)" }}
        data-testid="missioni-peers-preview"
      >
        <button
          onClick={() => { hapticTap(); setOpen((v) => !v); }}
          data-testid="missioni-peers-toggle"
          className="w-full flex items-center gap-2 px-3 py-2 text-left"
        >
          <span className="relative shrink-0">
            <span className="block h-2 w-2 rounded-full bg-[#2DBA60] animate-pulse" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-[12px] font-semibold text-[#1A1A1A] leading-tight">
              {count > 0
                ? <><span className="text-[#2DBA60]">{count}</span> padroni online vicino a te</>
                : "Nessuno visibile ora nel raggio 15 km"}
            </span>
            <span className="block text-[10px] text-[#7A7A7A] leading-tight mt-0.5">
              Raggio 15 km · aggiornamento ogni 45s
            </span>
          </span>
          {count > 0 && (
            open ? <ChevronUp className="h-4 w-4 text-[#7A7A7A]" /> : <ChevronDown className="h-4 w-4 text-[#7A7A7A]" />
          )}
        </button>
        <AnimatePresence initial={false}>
          {open && count > 0 && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.22 }}
              style={{ overflow: "hidden" }}
            >
              <div className="px-3 pb-3 border-t border-[#F0EBE3] max-h-[240px] overflow-y-auto space-y-1.5 pt-2">
                {peers.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`missioni-peer-${p.id}`}
                    className="flex items-center gap-2.5 px-1.5 py-1.5 rounded-xl hover:bg-[#F5EFE6] transition-colors"
                  >
                    <div className="relative shrink-0">
                      <div
                        className="h-9 w-9 rounded-full overflow-hidden border-2 border-white flex items-center justify-center text-[11px] font-semibold text-white"
                        style={{
                          background: p.photo_url ? "transparent" : "linear-gradient(135deg,#34D399,#2DBA60)",
                          boxShadow: "0 4px 10px -4px rgba(45,186,96,0.4)",
                        }}
                      >
                        {p.photo_url
                          ? <img src={p.photo_url} alt={p.name || ""} className="w-full h-full object-cover" />
                          : <span>{(p.name || "?").split(" ").slice(0,2).map(w=>w[0]).join("").toUpperCase()}</span>}
                      </div>
                      <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-[#2DBA60] border-2 border-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-semibold text-[#1A1A1A] leading-tight truncate">
                        {p.name || "Utente ORIO"}
                      </div>
                      {p.dog?.name ? (
                        <div className="text-[10.5px] text-[#7A7A7A] leading-tight truncate">
                          🐾 {p.dog.name}{p.dog.breed ? ` · ${p.dog.breed}` : ""}
                        </div>
                      ) : (
                        <div className="text-[10.5px] text-[#B0A084] leading-tight">Nella tua zona</div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[11px] font-semibold text-[#2DBA60] leading-tight">{p.distance_km} km</div>
                      <div className="text-[9.5px] text-[#B0A084] leading-tight">online</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
